<!--
SelfAwareItem Component
========================
Viewport-aware performance optimization component that conditionally renders content
based on visibility relative to the browser viewport or a custom scroll container.

Architecture: Shared Observer Pool
-----------------------------------
Instead of one IntersectionObserver and one ResizeObserver per component instance,
this component uses a module-level pool of shared observers keyed by (root, margin).
Components with the same config reuse a single IntersectionObserver, reducing observer
count from O(n instances) to O(unique configs).

Each shared IO is ref-counted: created on first subscriber, disconnected and removed
from the pool when the last subscriber unregisters. This prevents the pool from
accumulating stale, inert observers across mount/unmount cycles.

The ResizeObserver is a single global instance that batches all height measurements
into one rAF flush per frame, and is likewise ref-counted (destroyed at zero subscribers).

Visibility Hysteresis
---------------------
- Content SHOWS when it enters the `showMargin` region (inner threshold)
- Content HIDES when it exits the `hideMargin` region (outer threshold)
- showMargin < hideMargin creates a dead zone that prevents boundary flickering

Emits:
'visible' — { height: Number, isHidden: false } — content became visible
'hidden'  — { height: Number, isHidden: true  } — content replaced by ghost

Fixes applied over previous version (SelfAwareItem rev 1):
- Non-reactive internal state moved out of data() into created()
Vue 2 silently skips reactivity for _-prefixed properties; they don't belong in data().
reconnectTimeout also has no template dependency — moved alongside them.
- elementMap.set(el, this) now called exactly once per initObservers() cycle
(was redundantly called a second time inside subscribeResize)
- findScrollParent now checks document.body itself
The prior `while (node !== document.body)` condition exited before evaluating body,
so a body with overflow:scroll was silently skipped and fell back to viewport.
- Shared IntersectionObservers are now ref-counted via IOEntry objects
Previously, the ioPool Map entries were never removed — inert observers accumulated
indefinitely across mount/unmount cycles. IOEntry tracks subscriber count and
disconnects + removes itself from the pool when count reaches zero.
- immediate prop now uses a targeted re-observe instead of full reconnect
The prior implementation called debouncedReconnect() from a rAF, which tore down
and rebuilt all observers immediately after mount. The new _reobserve() method
simply unobserves and re-observes both IOs to force a fresh intersection evaluation
with no setup/teardown overhead.
- Dev warning added for observeResize:false + estimatedHeight:0 combination
Without resize observation and a non-zero estimated height, the ghost placeholder
collapses to 0px and causes scroll-position jumps on first hide.
- _forceShow() guards against destroyed instance before emitting
-->

<template lang="pug">
span.self-aware-item(
    ref="wrapper"
    :style="wrapperStyle"
)
    span.self-aware-item__ghost(
    v-if="enabled && isHidden"
    :style="{ height: ghostHeight + 'px' }"
    )
    slot(v-else)
</template>

<script>
// =============================================================================
// Module-level Shared Observer Pool
// =============================================================================

/**
 * Maps observed DOM elements → their Vue component instances.
 * Used by shared IO callbacks to dispatch show/hide to the right component.
 */
const elementMap = new WeakMap();

/**
 * Cache for resolved scroll parents to prevent layout thrashing on mount.
 * Caches the nearest scrollable ancestor for a given parent element.
 */
const scrollParentCache = new WeakMap();

/**
 * Detect whether we are running inside an iframe.
 * Cached at module load — avoids repeated checks in _resolveRoot().
 *
 * Uses try/catch because cross-origin iframes throw a SecurityError
 * when accessing window.top. In that case we assume iframe context.
 */
let _isInIframe = false;
try {
    _isInIframe = typeof window !== 'undefined' && window.self !== window.top;
} catch (e) {
    _isInIframe = true;
}

/**
 * IOEntry — wraps a shared IntersectionObserver with ref-counting.
 *
 * A single IOEntry is shared by all components with the same (type, root, margin).
 * It tracks how many elements are currently observed. When count reaches zero
 * the observer is disconnected and the entry is removed from the pool, preventing
 * accumulation of inert observers across mount/unmount cycles.
 */
class IOEntry {
    /**
     * @param {IntersectionObserver} io
     * @param {Map}                  marginMap - parent map this entry lives in
     * @param {string}               margin    - key within marginMap (for deletion)
     */
    constructor(io, marginMap, margin) {
    this.io        = io;
    this.marginMap = marginMap;
    this.margin    = margin;
    this.count     = 0;
    }

    observe(el) {
    this.count++;
    this.io.observe(el);
    }

    unobserve(el) {
    this.io.unobserve(el);
    this.count--;
    if (this.count <= 0) {
        this.io.disconnect();
        this.marginMap.delete(this.margin);
    }
    }
}

/**
 * Two-tier pool for shared IntersectionObservers.
 *
 * Structure: WeakMap<root, Map<margin, IOEntry>>
 *
 * WeakMap as outer key: when a scroll-container root is GC'd, its entire
 * inner Map (and all IOEntries) are collected automatically — no manual
 * cleanup needed for the root level.
 *
 * IOEntry ref-counting handles cleanup at the margin level.
 *
 * Viewport (root=null) cannot be a WeakMap key directly, so we use a
 * stable module-level sentinel object instead.
 */
const VIEWPORT_SENTINEL = {};
const ioPool = {
    show: new WeakMap(),
    hide: new WeakMap(),
};

/**
 * Get or create a ref-counted IOEntry for a (type, root, margin) triple.
 *
 * @param {'show'|'hide'} type
 * @param {Element|null}  root   - null means browser viewport
 * @param {string}        margin - CSS margin string e.g. "400px"
 * @returns {IOEntry}
 */
function getOrCreateIOEntry(type, root, margin) {
    const cacheKey  = root != null ? root : VIEWPORT_SENTINEL;
    const outerPool = ioPool[type];

    let marginMap = outerPool.get(cacheKey);
    if (!marginMap) {
    marginMap = new Map();
    outerPool.set(cacheKey, marginMap);
    }

    let entry = marginMap.get(margin);
    if (!entry) {
    const io = new IntersectionObserver(
        (entries) => {
        for (const entry of entries) {
            const inst = elementMap.get(entry.target);
            if (!inst) continue;
            if (type === "show" && entry.isIntersecting)  inst._show();
            if (type === "hide" && !entry.isIntersecting) inst._hide();
        }
        },
        {
        root,
        rootMargin: `${margin} 0px ${margin} 0px`,
        threshold: 0,
        }
    );
    entry = new IOEntry(io, marginMap, margin);
    marginMap.set(margin, entry);
    }

    return entry;
}

// =============================================================================
// Global ResizeObserver — single instance, ref-counted
// =============================================================================

let   _globalRO    = null; // Shared ResizeObserver instance
let   _globalCount = 0;    // Active subscriber count
let   _resizeRaf   = null; // rAF handle for batched height commits

/** Pending height updates collected this frame: element → newHeight (px) */
const _pending = new Map();

/**
 * Register an element with the global ResizeObserver.
 * Creates the observer on first subscriber; destroys it at zero subscribers.
 *
 * @param {Element} el
 * @param {object}  instance - Vue component instance
 */
function subscribeResize(el, instance) {
    if (!_globalRO) {
    _globalRO = new ResizeObserver((entries) => {
        for (const entry of entries) {
        const inst = elementMap.get(entry.target);
        if (!inst) continue;

        const h = entry.borderBoxSize
            ? entry.borderBoxSize[0].blockSize
            : entry.contentRect.height;

        if (inst.isHidden) {
            // Chrome bug workaround: IntersectionObserver can silently stop firing
            // after slot content is replaced with a non-zero-height element while
            // the wrapper was hidden. Force a fresh intersection evaluation by
            // briefly re-observing through both IOEntries.
            if (h > 0) {
            inst._reobserve(entry.target);
            }
            // Never update lockedHeight while hidden — would read ghost height.
            continue;
        }

        if (h > 0) _pending.set(entry.target, Math.round(h));
        }

        // Batch all lockedHeight updates into one rAF to avoid layout thrashing.
        if (_pending.size > 0 && !_resizeRaf) {
        _resizeRaf = requestAnimationFrame(() => {
            for (const [target, newH] of _pending) {
            const inst = elementMap.get(target);
            if (inst && !inst.isHidden && inst.lockedHeight !== newH) {
                inst.lockedHeight = newH;
            }
            }
            _pending.clear();
            _resizeRaf = null;
        });
        }
    });
    }

    _globalCount++;
    _globalRO.observe(el);
}

/**
 * Unsubscribe an element from the global ResizeObserver.
 * Destroys the observer when the last subscriber unregisters.
 *
 * @param {Element} el
 */
function unsubscribeResize(el) {
    if (!_globalRO) return;
    _globalRO.unobserve(el);
    _globalCount--;
    if (_globalCount <= 0) {
    _globalRO.disconnect();
    _globalRO = null;
    _globalCount = 0;
    if (_resizeRaf) { cancelAnimationFrame(_resizeRaf); _resizeRaf = null; }
    _pending.clear();
    }
}

// =============================================================================
// Component
// =============================================================================

export default {
    name: "SelfAwareItem",

    props: {
    /**
     * Estimated height of content (px).
     * Used as ghost placeholder fallback before ResizeObserver measures the real
     * height. Prevents scroll-position jumps on first hide.
     *
     * Required (with a non-zero value) when observeResize is false — otherwise
     * the ghost collapses to 0px and causes scroll jumps.
     */
    estimatedHeight: {
        type: Number,
        default: 0,
    },

    /**
     * Distance from the viewport/root edge at which content becomes visible.
     * Must be smaller than hideMargin to create the hysteresis dead zone.
     * Accepts any valid CSS margin value (e.g. "400px").
     */
    showMargin: {
        type: String,
        default: "400px",
    },

    /**
     * Distance from the viewport/root edge at which content is hidden.
     * Must be larger than showMargin to prevent boundary flickering.
     * Accepts any valid CSS margin value (e.g. "800px").
     */
    hideMargin: {
        type: String,
        default: "800px",
    },

    /**
     * Enable or disable the virtualization optimization.
     * When false, content always renders and no observers are attached.
     */
    enabled: {
        type: Boolean,
        default: true,
    },

    /**
     * Custom scroll container for the IntersectionObserver root.
     * Expected type: HTMLElement | null
     * If null and viewport is false, the nearest scrollable ancestor is used.
     *
     * Note: Vue 2 has no HTMLElement type — Object is the closest native type.
     * Passing a non-Element value triggers a dev-mode console warning and falls
     * back to nearest-ancestor auto-detection.
     */
    scrollRoot: {
        type: Object,
        default: null,
    },

    /**
     * When true, uses the browser viewport as the IntersectionObserver root,
     * ignoring scrollRoot and nearest-ancestor auto-detection.
     */
    viewport: {
        type: Boolean,
        default: false,
    },

    /**
     * When true, a ResizeObserver tracks content height while visible,
     * keeping the ghost placeholder accurate after dynamic content changes.
     *
     * When false, set estimatedHeight to a non-zero value or the ghost
     * placeholder will be 0px and cause scroll-position jumps on first hide.
     */
    observeResize: {
        type: Boolean,
        default: true,
    },

    /**
     * When true, forces a fresh intersection check on the next animation frame
     * after mount. Useful when the component mounts into an already-scrolled
     * container where the initial IntersectionObserver callback may not fire.
     *
     * Uses a targeted re-observe (not a full reconnect) so there is no
     * setup/teardown overhead beyond two unobserve+observe calls.
     */
    immediate: {
        type: Boolean,
        default: false,
    },
    },

    data() {
    return {
        // These two are reactive because they drive the template.
        isHidden:     false,
        lockedHeight: this.estimatedHeight || 0,
    };
    },

    computed: {
    /**
     * Height for the ghost placeholder.
     * Prefers ResizeObserver-measured value; falls back to estimatedHeight prop.
     */
    ghostHeight() {
        return this.lockedHeight || this.estimatedHeight || 0;
    },

    /**
     * Reserves vertical space while content is hidden to prevent scroll jumps.
     * Returns undefined (not { minHeight: undefined }) so Vue skips the
     * attribute binding entirely when no style is needed.
     */
    wrapperStyle() {
        return this.isHidden && this.ghostHeight
        ? { minHeight: `${this.ghostHeight}px` }
        : undefined;
    },
    },

    watch: {
    enabled(val) {
        if (val) {
        this.debouncedReconnect();
        } else {
        this.disconnectObservers();
        this._forceShow();
        }
    },
    hideMargin()    { this.debouncedReconnect(); },
    showMargin()    { this.debouncedReconnect(); },
    viewport()      { this.debouncedReconnect(); },
    scrollRoot()    { this.debouncedReconnect(); },
    observeResize() { this.debouncedReconnect(); },
    },

    created() {
    // Internal bookkeeping — none of these need reactivity.
    // Vue 2 silently skips reactivity for _-prefixed names and does not proxy
    // them, so declaring them in data() is misleading. Initialise directly here.
    this._showEntry       = null; // IOEntry for show observer
    this._hideEntry       = null; // IOEntry for hide observer
    this._hasResize       = false; // Whether subscribeResize() is active
    this._reconnectTimer  = null; // setTimeout handle for debouncedReconnect
    },

    mounted() {
    if (process.env.NODE_ENV === "development") {
        if (!this.observeResize && !this.estimatedHeight) {
        console.warn(
            "[SelfAwareItem] observeResize is false and estimatedHeight is 0. " +
            "The ghost placeholder will collapse to 0px and cause scroll-position jumps. " +
            "Set a non-zero estimatedHeight or enable observeResize."
        );
        }
    }

    this.initObservers();

    if (this.immediate) {
        // Force a fresh intersection evaluation on the next rAF without rebuilding
        // observers. Catches components that mount into already-scrolled containers
        // where the initial IO callback never fires.
        requestAnimationFrame(() => this._reobserve());
    }
    },

    beforeDestroy() {
    // CRITICAL ORDER: cancel the reconnect timer FIRST.
    // If it fires after disconnectObservers() has cleared the entries,
    // initObservers() would run against a destroyed component.
    if (this._reconnectTimer) clearTimeout(this._reconnectTimer);
    this.disconnectObservers();
    },

    methods: {
    // ─── Observer Lifecycle ─────────────────────────────────────────────────

    /** Attach all observers for this element. */
    initObservers() {
        try {
        if (
            !this.enabled ||
            typeof window === "undefined" ||
            !("IntersectionObserver" in window) ||
            !("ResizeObserver" in window) ||
            !this.$refs.wrapper
        ) {
            this._forceShow();
            return;
        }

        // Iframe + viewport mode: IntersectionObserver with root:null
        // misreports visibility inside iframes (the root viewport doesn't
        // match the iframe's visible area), and findScrollParent() cannot
        // reliably identify a correct alternative root. Skip virtualization
        // entirely — content always renders. This prevents both flickering
        // and disappearing content in iframe deployments.
        if (this.viewport && _isInIframe) {
            return; // content stays visible (isHidden defaults to false)
        }

        const el   = this.$refs.wrapper;
        const root = this._resolveRoot();

        // Register in elementMap once — subscribeResize must not re-set this.
        elementMap.set(el, this);

        // Show entry: fires _show() when element enters the showMargin region.
        this._showEntry = getOrCreateIOEntry("show", root, this.showMargin);
        this._showEntry.observe(el);

        // Hide entry: fires _hide() when element exits the hideMargin region.
        // The showMargin < hideMargin gap is the hysteresis dead zone.
        this._hideEntry = getOrCreateIOEntry("hide", root, this.hideMargin);
        this._hideEntry.observe(el);

        // ResizeObserver: maintains lockedHeight while content is visible.
        // elementMap is already set above — subscribeResize uses it, not sets it.
        if (this.observeResize) {
            subscribeResize(el, this);
            this._hasResize = true;
        }
        } catch (err) {
        if (process.env.NODE_ENV === "development") {
            console.error("[SelfAwareItem] initObservers failed:", err);
        }
        // Safe fallback: always render content if observers fail to initialise.
        this._forceShow();
        }
    },

    /** Detach all observers and remove element from the callback map. */
    disconnectObservers() {
        const el = this.$refs.wrapper;
        if (el) {
        if (this._showEntry) this._showEntry.unobserve(el);
        if (this._hideEntry) this._hideEntry.unobserve(el);
        if (this._hasResize) {
            unsubscribeResize(el);
            this._hasResize = false;
        }
        // Remove from elementMap so no stale callbacks fire post-disconnect.
        elementMap.delete(el);
        }
        this._showEntry = null;
        this._hideEntry = null;
    },

    /**
     * Debounce observer reconnection so rapid prop changes (e.g. showMargin +
     * hideMargin updated in the same tick) only trigger one reconnect cycle.
     */
    debouncedReconnect() {
        if (this._reconnectTimer) clearTimeout(this._reconnectTimer);
        this._reconnectTimer = setTimeout(() => {
        this.disconnectObservers();
        this.initObservers();
        }, 16); // ~1 rAF
    },

    // ─── Visibility State ───────────────────────────────────────────────────

    /**
     * Make content visible. Idempotent — no-op if already visible.
     * Called by the shared IO callback.
     */
    _show() {
        if (!this.enabled || !this.isHidden) return;
        this.isHidden = false;
        this.$emit("visible", { height: this.ghostHeight, isHidden: false });
    },

    /**
     * Replace content with ghost placeholder. Idempotent — no-op if already hidden.
     * Does NOT read offsetHeight — lockedHeight is maintained by the global
     * ResizeObserver so no synchronous layout reflow is needed here.
     */
    _hide() {
        if (!this.enabled || this.isHidden) return;
        this.isHidden = true;
        this.$emit("hidden", { height: this.ghostHeight, isHidden: true });
    },

    /**
     * Force content visible regardless of enabled/observer state.
     * Used on error and feature-detection fallback paths.
     * Guards against post-destroy emission.
     */
    _forceShow() {
        if (this._isDestroyed || !this.isHidden) return;
        this.isHidden = false;
        this.$emit("visible", { height: this.ghostHeight, isHidden: false });
    },

    /**
     * Force a fresh intersection evaluation without rebuilding observers.
     * Briefly unobserves and re-observes both IOs — the browser re-runs the
     * intersection check on the next frame. Used by the Chrome re-observe
     * workaround (via ResizeObserver callback) and the `immediate` prop.
     *
     * Much cheaper than debouncedReconnect() for cases where the observer
     * config has not changed and only a fresh check is needed.
     *
     * @param {Element} [el] - target element; defaults to this.$refs.wrapper
     */
    _reobserve(el) {
        const target = el || this.$refs.wrapper;
        if (!target) return;
        if (this._showEntry) {
        this._showEntry.io.unobserve(target);
        this._showEntry.io.observe(target);
        }
        if (this._hideEntry) {
        this._hideEntry.io.unobserve(target);
        this._hideEntry.io.observe(target);
        }
    },

    // ─── Public API ─────────────────────────────────────────────────────────

    /**
     * Force a height remeasure from the live DOM.
     *
     * Useful after dynamic slot content changes that resize without triggering
     * the ResizeObserver (e.g. images loading, async data, CSS transitions).
     *
     * No-op when hidden — offsetHeight while hidden would return the ghost
     * placeholder height and silently corrupt lockedHeight.
     */
    remeasure() {
        if (this.isHidden) return;
        this.$nextTick(() => {
        const h = (this.$refs.wrapper && this.$refs.wrapper.offsetHeight) || 0;
        if (h > 0 && this.lockedHeight !== h) this.lockedHeight = h;
        });
    },

    // ─── Private Helpers ────────────────────────────────────────────────────

    /**
     * Resolve the IntersectionObserver root with explicit priority:
     *   1. viewport prop   → browser viewport (root: null)
     *   2. scrollRoot prop → provided element if it is a valid Element
     *   3. Auto-detect     → walk DOM to find nearest scrollable ancestor
     *
     * Note: iframe + viewport=true is handled in initObservers() which skips
     * observer setup entirely, so this method is never called in that case.
     *
     * @returns {Element|null}
     */
    _resolveRoot() {
        if (this.viewport) return null;

        if (this.scrollRoot != null) {
        if (this.scrollRoot instanceof Element) return this.scrollRoot;
        if (process.env.NODE_ENV === "development") {
            console.warn(
            "[SelfAwareItem] scrollRoot must be an HTMLElement. Received:",
            this.scrollRoot,
            "— falling back to nearest-ancestor auto-detection."
            );
        }
        }

        return this.findScrollParent(this.$refs.wrapper);
    },

    /**
     * Walk up the DOM tree to find the nearest ancestor with scrollable overflow.
     *
     * Walks from the element's parentElement up to and INCLUDING document.body.
     * The previous version used `while (node !== document.body)` which exited
     * before ever checking body — if body has overflow:scroll it was silently
     * skipped and the function incorrectly fell back to viewport.
     *
     * Only checks overflow and overflowY — overflowX is excluded because
     * horizontal scrollers are not valid roots for vertical virtualization and
     * produce false positives (e.g. overflow-x:auto on sticky navbars).
     *
     * Uses a scrollParentCache to drastically speed up sequential mounts and 
     * prevent layout thrashing in large lists.
     *
     * @param   {Element}       el
     * @returns {Element|null}  Nearest scrollable ancestor, or null (→ viewport)
     */
    findScrollParent(el) {
        if (!el || !el.parentElement) return null;
        
        let node = el.parentElement;
        // Fast path: reuse cached result if available
        if (scrollParentCache.has(node)) {
        return scrollParentCache.get(node);
        }
        
        let current = node;
        let found = null;
        
        while (current) {
        const { overflow, overflowY } = getComputedStyle(current);
        if (/(auto|scroll|overlay)/.test(overflow + overflowY)) {
            found = current;
            break;
        }
        if (current === document.body) break; // checked body — stop here
        current = current.parentElement;
        }
        
        // Cache the result for this parent node to accelerate future lookups
        scrollParentCache.set(node, found);
        return found;
    },
    },
};
</script>

<style scoped>
.self-aware-item {
    width: 100%;
    position: relative;
    /*
    No content-visibility: auto.
    That property reserves space via contain-intrinsic-size, which double-counts
    vertical space alongside our manual minHeight ghost approach, causing layout
    inconsistencies and scroll-position drift. JS owns this optimization fully.

    No will-change: contents.
    The shared observer architecture already minimises re-render cost. Applying
    will-change: contents to every item promotes each to its own compositor layer,
    increasing GPU memory on large lists with no measurable render benefit.
    */
}

.self-aware-item__ghost {
    width: 100%;
    display: inline-block;
}
</style>
